import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ShareButton from "@/components/ShareButton";
import BackToTopButton from "@/components/BackToTopButton";
import { useAlertCount } from "@/components/AlertsPanel";
import { useSEO } from "@/hooks/useSEO";
import { useI18n } from "@/contexts/I18nContext";
import { scrollToPageTop } from "@/lib/scroll";
import { BEACH_GUIDES, localizeBeach } from "@/data/beachGuide";
import LiveSurfReport from "@/components/LiveSurfReport";
import { Waves, ArrowRight, Compass } from "lucide-react";

/**
 * Public landing page listing every surf beach we have a guide for.
 * Big SEO surface + navigational hub for the /surf/:slug detail pages.
 */
export default function BeachesIndex() {
  const alertCount = useAlertCount();
  const { lang } = useI18n();
  React.useEffect(() => { scrollToPageTop(); }, []);

  useSEO({
    title: "Popoyo Surf Beaches — Complete Guide · Popoyo.app",
    description:
      "Complete guide to every surf beach near Popoyo, Nicaragua — Playa Popoyo, Playa Colorado, Playa Santana, Playa Guasacate, Panga Drops, and Playa Gigante. Live conditions, tides, best seasons, and how to get there.",
    path: "/beaches",
  });

  const beaches = Object.values(BEACH_GUIDES).map((b) => localizeBeach(b, lang));

  return (
    <div className="min-h-screen flex flex-col" data-testid="beaches-index-page">
      <Header alertCount={alertCount} />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        {/* Live surf cam at the very top of the surf page */}
        <div id="live-surf" className="scroll-mt-24">
          <LiveSurfReport />
        </div>

        {/* Hero */}
        <section className="clay p-6 sm:p-8">
          <p className="eyebrow text-[color:var(--coral)]">Surf beaches · Popoyo, Nicaragua</p>
          <div className="mt-1 flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight"
                style={{ fontFamily: "Outfit" }}
                data-testid="beaches-title"
              >
                Every surf beach near Popoyo
              </h1>
              <p className="text-base sm:text-lg text-[color:var(--ink-2)] mt-3 max-w-2xl">
                From the world-class A-frame reef at Playa Popoyo to the mellow beach break at Playa Guasacate — the complete local guide to surfing around Popoyo & Tola, Nicaragua. Real conditions, real seasons, real directions.
              </p>
            </div>
            <div className="shrink-0">
              <ShareButton
                path="/beaches"
                title="Popoyo Surf Beaches — Complete Guide · Popoyo.app"
                text="The complete local guide to every surf beach near Popoyo, Nicaragua."
                testId="share-beaches-btn"
              />
            </div>
          </div>
        </section>

        {/* Beach grid */}
        <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="beaches-grid">
          {beaches.map((b) => (
            <Link
              key={b.slug}
              to={`/surf/${b.slug}`}
              data-testid={`beach-card-${b.slug}`}
              className="clay overflow-hidden group hover:scale-[1.015] transition-transform"
            >
              {/* Photo cover */}
              <div
                className="relative h-52 sm:h-56 w-full"
                style={{
                  backgroundImage: b.photoUrl ? `url(${b.photoUrl})` : `linear-gradient(135deg, ${b.color}, ${b.color}CC)`,
                  backgroundSize: "cover",
                  backgroundPosition: "center 55%",
                }}
              >
                <div
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.55) 100%)" }}
                  aria-hidden
                />
                <div className="absolute inset-0 p-4 flex flex-col justify-end">
                  <p className="text-xs uppercase tracking-widest text-white/85">Surf guide</p>
                  <h2
                    className="text-2xl sm:text-3xl font-bold text-white leading-tight"
                    style={{ fontFamily: "Outfit", textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}
                  >
                    {b.name}
                  </h2>
                </div>
                <div
                  className="absolute top-3 right-3 rounded-full text-[11px] font-semibold text-white px-2.5 py-1"
                  style={{ background: `${b.color}` }}
                >
                  {b.difficulty}
                </div>
              </div>
              {/* Meta */}
              <div className="p-4">
                <p className="text-sm text-[color:var(--ink)] leading-snug">{b.tagline}</p>
                <div className="mt-3 flex items-center gap-3 text-xs text-[color:var(--ink-2)]">
                  <span className="inline-flex items-center gap-1">
                    <Waves className="w-3.5 h-3.5" />
                    {b.waveStyle.split(",")[0]}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 font-semibold text-[color:var(--coral)] group-hover:underline">
                    Read the guide <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </section>

        {/* Where to start */}
        <section className="clay p-5 sm:p-6">
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-[color:var(--coral)]" />
            <p className="eyebrow text-[color:var(--coral)]">Not sure where to start?</p>
          </div>
          <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
            <li>
              🥇 <strong>New to surfing?</strong> Start at <Link to="/surf/guasacate" className="text-[color:var(--coral)] underline">Playa Guasacate</Link> — long, sandy, forgiving beach break with easy vibes.
            </li>
            <li>
              🏄 <strong>Confident intermediate?</strong> <Link to="/surf/popoyo" className="text-[color:var(--coral)] underline">Playa Popoyo</Link> is the iconic world-class A-frame reef.
            </li>
            <li>
              🌊 <strong>Chasing bigger swell?</strong> <Link to="/surf/colorado" className="text-[color:var(--coral)] underline">Playa Colorado</Link> barrels hard, and <Link to="/surf/panga-drops" className="text-[color:var(--coral)] underline">Panga Drops</Link> holds size beautifully.
            </li>
            <li>
              🎣 <strong>Boat trips & fishing?</strong> Base yourself at <Link to="/surf/gigante" className="text-[color:var(--coral)] underline">Playa Gigante</Link> — a mellow bay with panga charters to the outer reefs.
            </li>
          </ul>
        </section>
      </main>

      <BackToTopButton />
      <Footer />
    </div>
  );
}
